import React, {
  useCallback,
  useMemo,
  useState
} from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import {
  SubmitHandler,
  useForm
} from "react-hook-form";
import { toast } from "react-toastify";

import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Text } from "src/library/components/Text";
import {
  SmartSwitch,
  SmartSwitchClass
} from "src/modules/smart/components/SmartSwitch";
import {
  IUser,
  OrderCutOffRemindersKeys,
  UserSettingName,
  IUserSettingAttributes,
  useUserApi
} from "src/core/users/api/users.api";
import { IStore } from "src/core/shared/store";
import { setGlobalUser } from "src/core/shared/store/global/global.actions";
import {
  CheckboxLabel,
  CheckboxStyled
} from "src/library/components/Forms";
import { Button } from "src/library/components/Button";
import { breakpoint } from "src/library/utils";

interface UserSmartSettingsFormProps {
  // suggestedOrders: boolean;
  orderCutOffReminders: boolean;
  onlineBanner: boolean;
  email: boolean;
}

const UserSmartSettingsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { register, handleSubmit } = useForm<UserSmartSettingsFormProps>();
  const dispatch = useDispatch();
  const [ loading, setLoading ] = useState(false);
  // const [ suggestedOrdersState, setSuggestedOrdersState ] = useState(false);
  const [ orderCutOffRemindersState, setOrderCutOffRemindersState ] = useState(false);
  const [ onlineBannerState, setOnlineBannerState ] = useState(false);
  const [ emailState, setEmailState ] = useState(false);
  const [ updateUserRes, updateUserReq ] = useUserApi("UPDATE_USER_SETTINGS");
  const { currentUser } = useMappedState(({ global }: IStore) => ({ currentUser: global.user as IUser }));

  // Compile the current user settings
  const currentUserSettings = useMemo(() => {
    const userSettings = [];

    // If there are no userSettings, return an empty array
    if (!currentUser.userSettings) {
      return [];
    }

    // Loop and find which settings are enabled
    for (const i in currentUser.userSettings) {
      const settings = currentUser.userSettings[ i ];

      // If the settings are enabled, add them to our user.
      if (settings.enabled) {
        userSettings.push(settings.settingName);
      }

      // if order cut off reminders, check options
      if (settings.settingName === UserSettingName.OrderCutOffReminders && settings.options) {
        for (const optionKey in settings.options) {
          if (settings.options[ optionKey ]) {
            userSettings.push(optionKey);
          }
        }
      }
    }

    return userSettings;
  }, [ currentUser ]);

  // handle submission
  const handleOnFormSubmit: SubmitHandler<UserSmartSettingsFormProps> = useCallback(
    () => {
      setLoading(true);

      const updatedSettings: Array<Omit<IUserSettingAttributes, "id">> = [
        // TODO: awaiting suggested orders
        // {
        //   userId: currentUser.id,
        //   settingName: UserSettingName.SuggestedOrders,
        //   enabled: suggestedOrdersState
        // },
        {
          userId: currentUser.id,
          settingName: UserSettingName.OrderCutOffReminders,
          enabled: orderCutOffRemindersState,
          options: {
            onlineBanner: onlineBannerState,
            email: emailState
          }
        }
      ];

      updateUserReq({
        id: currentUser.id,
        updatedSettings
      });
    },
    [
      currentUser.id,
      emailState,
      onlineBannerState,
      orderCutOffRemindersState,
      updateUserReq
    ]
  );

  // set user setting states on mount
  React.useEffect(() => {
    // setSuggestedOrdersState(_.includes(currentUserSettings, UserSettingName.SuggestedOrders));
    setOrderCutOffRemindersState(_.includes(currentUserSettings, UserSettingName.OrderCutOffReminders));
    setOnlineBannerState(_.includes(currentUserSettings, OrderCutOffRemindersKeys.OnlineBanner));
    setEmailState(_.includes(currentUserSettings, OrderCutOffRemindersKeys.Email));
  }, [ currentUserSettings ]);

  // handle request response
  React.useEffect(() => {
    if (updateUserRes.error) {
      setLoading(false);
      toast.error(updateUserRes.error.message);
    }

    if (updateUserRes.data) {
      // Update the current user store
      dispatch(setGlobalUser(updateUserRes.data, true));
      setLoading(false);
      toast.success("Your user settings have updated successfully.");
    }
  }, [
    updateUserRes.error,
    updateUserRes.data,
    dispatch
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <PageContent>
          <div className="intro-content-wrapper">
            <SmartSubHeadingLogo>
              <div className="icon-wrapper">
                <Icon
                  icon={Icons.BOLT}
                  $fillColor="smartLight"
                  size={20}
                />
              </div>

              {" "}
              SMART
            </SmartSubHeadingLogo>

            <Text
              className="title"
              as="h2"
            >
              Smart Features
            </Text>

            <Text as="p">
              Smart features use state-of-the-art technologies such as Machine Learning and Artificial Intelligence to analyse your usage and improve your experience on our ordering platform.
            </Text>

            <Text as="p">
              We can then build smart algorithms to help you build orders, send reminders, suggest products and give you support throughout the process. Watch this space for updates and new smart features coming soon!
            </Text>
          </div>

          <form
            className="form-fields"
            onSubmit={handleSubmit(handleOnFormSubmit)}
          >
            {/* Suggested Orders todo: waiting on suggested orders functionality */}
            {/* <div className="form-field">
              <SmartSwitch
                className={suggestedOrdersState ? SmartSwitchClass.ON : SmartSwitchClass.OFF}
                checked={suggestedOrdersState}
                onChange={() => setSuggestedOrdersState(!suggestedOrdersState)}
              />
              <div className="form-field-information">
                <Text as="h4">
                  Suggested Orders
                </Text>
                <Text as="p">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </Text>
                <Text as="p">
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis  eu fugiat nulla pariatur.
                </Text>
              </div>
            </div> */}

            {/* Order Cut Off Reminders */}
            <div className="form-field">
              <SmartSwitch
                className={orderCutOffRemindersState ? SmartSwitchClass.ON : SmartSwitchClass.OFF}
                checked={orderCutOffRemindersState}
                onChange={() => setOrderCutOffRemindersState(!orderCutOffRemindersState)}
              />

              <div className="form-field-information">
                <Text as="h4">
                  Order Cut Off Reminders
                </Text>

                <Text as="p">
                  Based on previous order history receive reminder notifications for delivery dates where you may have forgotton to place an order, ensuring you never miss the cutoff time again!
                </Text>

                {orderCutOffRemindersState && (
                  <>
                    <CheckboxLabel
                      for="email"
                      label="Email"
                    >
                      <CheckboxStyled
                        className="smart-check-box"
                        ref={register()}
                        id="email"
                        name="email"
                        checked={(orderCutOffRemindersState && emailState)}
                        onChange={event => {
                          setEmailState(event.target.checked);
                        }}
                      />
                    </CheckboxLabel>

                    <CheckboxLabel
                      for="onlineBanner"
                      label="In-App Banner"
                    >
                      <CheckboxStyled
                        className="smart-check-box"
                        ref={register()}
                        id="onlineBanner"
                        name="onlineBanner"
                        checked={(orderCutOffRemindersState && onlineBannerState)}
                        onChange={event => {
                          setOnlineBannerState(event.target.checked);
                        }}
                      />
                    </CheckboxLabel>
                  </>
                )}
              </div>
            </div>

            <Button
              loading={loading}
              type="submit"
            >
              Save
            </Button>
          </form>
        </PageContent>
      </MainContentWrapper>
    </Layout>
  );
};

// This is the only place this flag is used and so not abstracted into reusable component.
const SmartSubHeadingLogo = styled.div`
  ${({ theme }) => css`
    background: transparent linear-gradient(90deg, ${theme.colors.smartLight} 0%, ${theme.colors.smartDark} 100%) 0% 0% no-repeat padding-box;
    font-size: 12px;
    height: 20px;
    width: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    letter-spacing: 6px;
    color: white;
    font-weight: 400;
    
    .icon-wrapper {
      background: white;
      border-radius: 50%;
      height: 25px;
      width: 25px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: ${theme.spacing.margins.xs};
    }
  `}
`;

const PageContent = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    background: white;
    border-radius: 5px;
    padding: ${theme.spacing.padding.lg};
    
    .intro-content-wrapper {
      ${breakpoint("xl", theme)} {
        max-width: 50%;
      }
    }
    
    .title {
      margin-top: ${theme.spacing.margins.lg};
    }
    
    .form-fields {
      margin-top: ${theme.spacing.margins.lg};
      ${breakpoint("xl", theme)} {
        max-width: 50%;
      }
    }
    
    .form-field {
      display: flex;
    }
    
    .form-field-information {
      margin-left: ${theme.spacing.margins.lg};
    }
    
    .form-field-information p {
      color: ${theme.colors.smartLightGrey}
    }
    
    .smart-check-box:hover {
      cursor: pointer;
    }
  `}
`;

export default UserSmartSettingsPage;
